import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, UserCheck, Shield, AlertTriangle, FileText } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const AgeVerificationPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-red-600 via-orange-600 to-amber-600 bg-clip-text text-transparent">
              Age Verification Policy
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            FlameLink is committed to maintaining a safe, adults-only platform. Learn about our age verification requirements and processes.
          </p>
          <p className="text-sm text-gray-500 mt-2">Effective Date: December 15, 2024 | Last Updated: December 15, 2024</p>
        </div>

        <div className="space-y-8">
          {/* Age Requirement */}
          <Card className="p-8 bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
              18+ Only Platform
            </h2>
            
            <div className="space-y-4 text-gray-700">
              <div className="bg-white rounded-lg p-6 border-l-4 border-red-500">
                <h3 className="font-semibold text-red-800 mb-2 text-lg">Strict Age Requirement</h3>
                <p className="text-gray-700 mb-4">
                  FlameLink is exclusively for adults aged <strong>18 years and older</strong>. This policy is non-negotiable 
                  and applies globally to all users regardless of local age of consent laws.
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Minimum age: 18 years old on the date of registration</li>
                  <li>• No exceptions for parental consent or supervision</li>
                  <li>• Applies to all features including browsing, messaging, and tipping</li>
                  <li>• Violates terms of service to misrepresent age</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Verification Process */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <UserCheck className="w-6 h-6 text-blue-500 mr-3" />
              1. Age Verification Process
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1.1 Initial Registration Verification</h3>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm mr-3 mt-1">1</div>
                      <div>
                        <h4 className="font-medium text-gray-900">Date of Birth Declaration</h4>
                        <p className="text-gray-600 text-sm">Users must enter their date of birth during registration</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm mr-3 mt-1">2</div>
                      <div>
                        <h4 className="font-medium text-gray-900">Automatic Age Calculation</h4>
                        <p className="text-gray-600 text-sm">System automatically calculates age and blocks underage registrations</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm mr-3 mt-1">3</div>
                      <div>
                        <h4 className="font-medium text-gray-900">Legal Declaration</h4>
                        <p className="text-gray-600 text-sm">Users confirm they are 18+ and agree to age-related terms</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1.2 Enhanced Verification Requirements</h3>
                <p className="text-gray-700 mb-4">
                  FlameLink may require additional age verification in the following circumstances:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Profile photos appearing to show someone under 18</li>
                  <li>• User reports or complaints regarding age misrepresentation</li>
                  <li>• Suspicious account activity or registration patterns</li>
                  <li>• Random verification audits for platform safety</li>
                  <li>• Legal requirements in specific jurisdictions</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1.3 Document Verification Process</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Accepted Documents</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• Government-issued photo ID (driver's license)</li>
                      <li>• Passport or national ID card</li>
                      <li>• State-issued identification card</li>
                      <li>• Military identification</li>
                      <li>• Other government-issued photo identification</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Verification Standards</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• Clear, readable photo of entire document</li>
                      <li>• All four corners of ID must be visible</li>
                      <li>• No blurring, covering, or editing of information</li>
                      <li>• Document must be current and not expired</li>
                      <li>• Selfie may be required for identity matching</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Security and Privacy */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Shield className="w-6 h-6 text-green-500 mr-3" />
              2. Security and Privacy Protection
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.1 Data Protection Measures</h3>
                <div className="bg-green-50 rounded-lg p-4">
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <Shield className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <strong>Encryption:</strong> All uploaded documents are encrypted in transit and at rest using industry-standard AES-256 encryption
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <Shield className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <strong>Limited Access:</strong> Only authorized verification personnel can access submitted documents
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <Shield className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <strong>Secure Storage:</strong> Documents stored in isolated, secured systems with strict access controls
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <Shield className="w-4 h-4 text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <strong>Automatic Deletion:</strong> Original documents deleted after verification completion (within 30 days)
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.2 Third-Party Verification Partners</h3>
                <p className="text-gray-700 mb-4">
                  FlameLink partners with certified identity verification services to ensure secure and accurate age verification:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Jumio Identity Verification</h4>
                    <p className="text-gray-600 text-sm">AI-powered ID verification with liveness detection and fraud prevention</p>
                  </div>
                  
                  <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Onfido Identity Checks</h4>
                    <p className="text-gray-600 text-sm">Document authentication and biometric verification services</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.3 Data Retention and Disposal</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• <strong>Verification Records:</strong> Age verification status retained for account lifetime</li>
                  <li>• <strong>Document Images:</strong> Automatically deleted within 30 days of verification</li>
                  <li>• <strong>Verification Logs:</strong> Technical verification records kept for 3 years (compliance)</li>
                  <li>• <strong>Failed Attempts:</strong> Failed verification attempts logged for fraud prevention (1 year)</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Enforcement and Consequences */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
              3. Enforcement and Consequences
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3.1 Age Misrepresentation Consequences</h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2">Immediate Actions for Underage Users</h4>
                  <ul className="space-y-2 text-red-700 text-sm">
                    <li>• Immediate permanent account suspension</li>
                    <li>• Complete profile removal from platform</li>
                    <li>• All data associated with account deleted</li>
                    <li>• Permanent ban from creating future accounts</li>
                    <li>• Legal guardians notified where legally required</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3.2 False Documentation Penalties</h3>
                <p className="text-gray-700 mb-4">
                  Users who submit false, altered, or fraudulent identification documents face:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Immediate account termination without appeal</li>
                  <li>• Permanent ban from FlameLink services</li>
                  <li>• Potential legal action for document fraud</li>
                  <li>• Reporting to relevant authorities where required</li>
                  <li>• Sharing of fraud indicators with industry partners</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3.3 Reporting and Detection Systems</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Automated Detection</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• AI-powered photo analysis for age estimation</li>
                      <li>• Machine learning algorithms detect suspicious patterns</li>
                      <li>• Automated flagging of potential underage accounts</li>
                      <li>• Cross-reference verification with external databases</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Community Reporting</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• User reporting system for suspected underage accounts</li>
                      <li>• 24/7 safety team review of age-related reports</li>
                      <li>• Anonymous reporting options available</li>
                      <li>• Fast-track investigation for age verification concerns</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Legal Compliance */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FileText className="w-6 h-6 text-purple-500 mr-3" />
              4. Legal Compliance
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.1 International Legal Requirements</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">United States</h4>
                    <ul className="space-y-1 text-gray-700 text-sm ml-4">
                      <li>• COPPA compliance (Children's Online Privacy Protection Act)</li>
                      <li>• State-specific age verification laws</li>
                      <li>• Federal online safety requirements</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">European Union</h4>
                    <ul className="space-y-1 text-gray-700 text-sm ml-4">
                      <li>• GDPR age of consent requirements (13-16 varies by country)</li>
                      <li>• Digital Services Act child protection measures</li>
                      <li>• National digital safety regulations</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Other Jurisdictions</h4>
                    <ul className="space-y-1 text-gray-700 text-sm ml-4">
                      <li>• Canada: Personal Information Protection and Electronic Documents Act</li>
                      <li>• Australia: Online Safety Act child protection requirements</li>
                      <li>• UK: Age Appropriate Design Code compliance</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.2 Industry Standards Compliance</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• NIST Cybersecurity Framework for identity verification</li>
                  <li>• ISO 27001 information security management standards</li>
                  <li>• FATF guidance on digital identity verification</li>
                  <li>• Dating industry safety best practices</li>
                  <li>• Age verification technology certification standards</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.3 Law Enforcement Cooperation</h3>
                <p className="text-gray-700 mb-4">
                  FlameLink cooperates fully with law enforcement regarding child safety:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Immediate reporting of suspected child exploitation</li>
                  <li>• Cooperation with investigations involving minors</li>
                  <li>• Data preservation for legal proceedings</li>
                  <li>• Compliance with lawful data requests</li>
                  <li>• Participation in industry safety initiatives</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Appeals Process */}
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Appeals and Support Process</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5.1 Verification Support</h3>
                <p className="text-gray-700 mb-4">
                  If you're having trouble with age verification, our support team can help:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li>• Technical assistance with document upload</li>
                  <li>• Alternative verification methods for special circumstances</li>
                  <li>• Guidance on acceptable documentation</li>
                  <li>• Troubleshooting verification failures</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5.2 Appeals Process (Limited)</h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Important:</strong> Age-related account suspensions have limited appeal options. 
                    Appeals are only considered for technical errors in the verification process, not for 
                    cases where users are actually under 18.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Need Help with Age Verification?</h3>
                <div className="text-blue-700 text-sm space-y-2">
                  <p>Contact our verification support team:</p>
                  <ul className="space-y-1">
                    <li>• Email: verification@flamelink.com</li>
                    <li>• Phone: +1 (555) 123-VERIFY</li>
                    <li>• Live Chat: Available in app during business hours</li>
                    <li>• Response Time: Within 24 hours for verification issues</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          {/* Contact */}
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Questions About Age Verification?</h3>
            <p className="text-gray-600 mb-6">
              Our verification team is here to help ensure a safe, adults-only platform
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.open('mailto:verification@flamelink.com', '_blank')}
                className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white"
              >
                Contact Verification Team
              </Button>
              <Button
                variant="outline"
                onClick={onBack}
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgeVerificationPolicy;